import React, { useEffect, useState } from "react";
import api, { getFullImageUrl } from "../services/api";
import FeaturedCarousel from "../components/FeaturedCarousel";
import Navbar from "../components/Navbar";
import BannerSlider from "../components/BannerSlider";
import useFooterConfig from "../hooks/useFooterConfig";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import ProductsNew from "../components/ProductsNew";
import SEO from "../components/SEO";
import LuckyWheel from "../components/LuckyWheel";
import BannerKM from "../assets/banner/banner km.png";
import FlashSaleBar from "../components/FlashSaleBar";
import FlashSaleProducts from "../components/FlashSaleProducts";
import MegaSaleBanner from "../components/MegaSaleBanner";
import PromoBanner from "../components/PromoBanner";
import CustomerReviews from "../components/CustomerReviews";
import ScrollReveal from "../components/ScrollReveal";
import SectionHeader from "../components/SectionHeader";
import VoucherSection from "../components/VoucherSection";
import HomePromoBanner from "../components/HomePromoBanner";

// Import Promo Images
import PromoImg1 from "../assets/banner/taha_promo_1.png";
import PromoImg2 from "../assets/banner/taha_promo_2.png";
import PromoImg3 from "../assets/banner/taha_promo_3.png";
import LogoTHS from "../assets/logo.png";

const Home = () => {
  const [sections, setSections] = useState([]);
  const [sectionData, setSectionData] = useState({}); // Key -> Products
  const [vouchers, setVouchers] = useState([]);
  const [userVouchers, setUserVouchers] = useState([]);
  const [banners, setBanners] = useState({});
  const [loading, setLoading] = useState(true);
  const { footerConfig } = useFooterConfig();

  const fetchHomeData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // 1. Fetch Sections config
      const sectionsRes = await api.get("/home-sections");
      const activeSections = sectionsRes.data.filter((s) => s.isActive);
      setSections(activeSections);

      // 2. Fetch Products for each section and Vouchers
      const productPromises = activeSections.map((s) =>
        api.get("/home-sections/products", { params: { key: s.key } }),
      );

      const [vRes, uvRes, bRes, ...pResults] = await Promise.allSettled([
        api.get("/vouchers"),
        token ? api.get("/vouchers/user") : Promise.reject("Not logged in"),
        api.get("/banners"),
        ...productPromises,
      ]);

      // Handle Vouchers
      if (vRes.status === "fulfilled") setVouchers(vRes.value.data || []);
      if (uvRes.status === "fulfilled") setUserVouchers(uvRes.value.data || []);
      
      // Handle Banners
      if (bRes.status === "fulfilled") setBanners(bRes.value.data || {});

      // Handle Section Products
      const newData = {};
      pResults.forEach((res, index) => {
        if (res.status === "fulfilled") {
          newData[activeSections[index].key] = res.value.data || [];
        }
      });
      setSectionData(newData);
    } catch (err) {
      console.error("Error fetching home data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomeData();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Helper to render section
  const renderSection = (key) => {
    const section = sections.find((s) => s.key === key);
    if (!section || !section.isActive) return null;

    const products = sectionData[key] || [];

    // Custom rendering based on key if needed
    if (key === "flash-sales") {
      return (
        <ScrollReveal variants="fade-up" threshold={0.2} key={key}>
          <FlashSaleBar title={section.title} />
          {loading ? (
            <div className="py-20 text-center">
              <div className="inline-block w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <FlashSaleProducts products={products} />
          )}
        </ScrollReveal>
      );
    }

    if (key === "featured") {
      return (
        <ScrollReveal variants="fade-up" threshold={0.2} key={key}>
          <section className="max-w-7xl mx-auto px-4 pt-4 sm:pt-14 pb-0 sm:pb-6 cursor-pointer">
            {products.length > 0 ? (
              <FeaturedCarousel products={products} title={section.title} />
            ) : null}
          </section>
        </ScrollReveal>
      );
    }

    // Default for newest, best-sellers, hot-trend, most-favorited
    const accentColors = {
      newest: "blue",
      "best-sellers": "red",
      "hot-trend": "dark",
      "most-favorited": "blue",
    };

    return (
      <ScrollReveal variants="fade-up" threshold={0.2} key={key}>
        <section className="max-w-7xl mx-auto px-4 pt-6 sm:pt-20 pb-4 cursor-pointer">
          <SectionHeader
            title={section.title.toUpperCase()}
            subtitle={`Khám phá bộ sưu tập ${section.title}`}
            accentColor={accentColors[key] || "blue"}
          />
          {products.length > 0 ? (
            <ProductsNew products={products} />
          ) : (
            <p className="text-center text-gray-500 dark:text-slate-500 py-10 font-medium">
              Không tìm thấy sản phẩm.
            </p>
          )}
        </section>
      </ScrollReveal>
    );
  };

  return (
    <div
      className="bg-white dark:bg-slate-950 min-h-screen flex flex-col transition-colors duration-300"
      style={{
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center",
      }}
    >
      <SEO
        title="Giày Thể Thao Chính Hãng & Sneaker Cao Cấp Việt Nam"
        description="TaHa Shoes - Hệ thống bán lẻ giày thể thao chính hãng uy tín từ Nike, Adidas, Jordan, Puma. Cam kết 100% Authentic, bảo hành 12 tháng, đổi trả trong 7 ngày. Giao hàng siêu tốc toàn quốc."
        keywords="giày thể thao chính hãng, sneaker cao cấp, taha shoes, mua giày nike chính hãng, mua giày adidas, jordan sneaker viet nam, shop giày uy tín thủ đức"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "TaHa Shoes",
            url: "https://tahashoes.vn",
            logo: "https://tahashoes.vn/logo.png",
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+84-xxx-xxx-xxx",
              contactType: "customer service",
              areaServed: "VN",
              availableLanguage: "Vietnamese",
            },
            sameAs: [
              "https://www.facebook.com/tahashoes",
              "https://www.instagram.com/tahashoes",
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "TaHa Shoes có bán giày chính hãng không?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Chúng tôi cam kết 100% sản phẩm là hàng Authentic chính hãng từ các thương hiệu Nike, Adidas, Jordan, Puma. Phát hiện hàng giả đền tiền gấp 10 lần.",
                },
              },
              {
                "@type": "Question",
                name: "Chính sách bảo hành tại TaHa Shoes như thế nào?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Tất cả sản phẩm tại TaHa Shoes được bảo hành chính hãng và hỗ trợ sửa chữa lỗi keo, chỉ trong vòng 12 tháng kể từ ngày mua hàng.",
                },
              },
              {
                "@type": "Question",
                name: "Thời gian giao hàng mất bao lâu?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Chúng tôi giao hàng siêu tốc trong 2-4 giờ tại nội thành TP.HCM và 2-4 ngày trên phạm vi toàn quốc.",
                },
              },
            ],
          },
        ]}
      />
      <Navbar />

      <main className="flex-grow">
        {loading && sections.length === 0 && (
          <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white/80 dark:bg-slate-950/80 backdrop-blur-md">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600 dark:text-slate-400 font-bold animate-pulse">
              Đang tải dữ liệu sản phẩm...
            </p>
          </div>
        )}

        <ScrollReveal variants="fade-in" duration={1.2}>
          <BannerSlider slides={banners["home-slider"]} />
        </ScrollReveal>

        <VoucherSection
          vouchers={vouchers}
          userVouchers={userVouchers}
          onRefresh={fetchHomeData}
        />

        {/* Dynamic Sections */}
        {renderSection("flash-sales")}

        <ScrollReveal variants="scale-up" threshold={0.3}>
          <MegaSaleBanner content={banners["mega-sale"]} />
        </ScrollReveal>

        {renderSection("featured")}

        <ScrollReveal variants="fade-up" threshold={0.2}>
          <section className="w-full py-6 sm:py-10 pt-0 sm:pt-2">
            <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {[
                {
                  img: "https://go-sporty.monamedia.net/wp-content/uploads/2025/04/ser_1.png",
                  title: "Vận chuyển siêu tốc",
                  desc: "Miễn phí toàn quốc",
                },
                {
                  img: "https://go-sporty.monamedia.net/wp-content/uploads/2025/04/ser_2-1000x999.png",
                  title: "Đổi trả dễ dàng",
                  desc: "Đổi size miễn phí 7 ngày",
                },
                {
                  img: "https://go-sporty.monamedia.net/wp-content/uploads/2025/04/ser_3.webp",
                  title: "Thanh toán linh hoạt",
                  desc: "An toàn & bảo mật",
                },
                {
                  img: "https://go-sporty.monamedia.net/wp-content/uploads/2025/04/ser_4.webp",
                  title: "Hỗ trợ 24/7",
                  desc: "Tư vấn mọi lúc",
                },
              ].map((item, i) => (
                <ScrollReveal
                  key={i}
                  variants="fade-up"
                  delay={i * 0.1}
                  distance={20}
                >
                  <div className="flex items-center gap-4 bg-gradient-to-r from-blue-700 to-blue-500 text-white py-4 px-5 rounded-2xl shadow-xl shadow-blue-500/20 cursor-pointer hover:scale-[1.02] transition-all border border-white/10">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl shrink-0 backdrop-blur-sm">
                      <img
                        src={item.img}
                        className="w-6 h-6 filter brightness-0 invert"
                        alt=""
                      />
                    </div>
                    <div>
                      <p className="font-black text-xs sm:text-sm uppercase tracking-wider">
                        {item.title}
                      </p>
                      <p className="text-[10px] sm:text-xs text-blue-50 font-medium">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal variants="scale-up" threshold={0.2}>
          <div className="max-w-7xl mx-auto py-2 sm:py-5 px-2 sm:px-4">
            <Link to={banners["banner-km"]?.link || "/products"}>
              <img
                src={getFullImageUrl(banners["banner-km"]?.image || BannerKM)}
                alt=""
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </Link>
          </div>
        </ScrollReveal>

        {renderSection("newest")}

        <ScrollReveal variants="fade-in" duration={1} threshold={0.2}>
          <PromoBanner content={banners["promo"]} />
        </ScrollReveal>

        {renderSection("best-sellers")}
        <HomePromoBanner
          image={getFullImageUrl(banners["home-promo-1"]?.image || PromoImg1)}
          logo={LogoTHS}
          title={banners["home-promo-1"]?.title || "SPRING SALE 2026"}
          subtitle={banners["home-promo-1"]?.description || "Đón mùa xuân rạng rỡ với những đôi sneaker phong cách nhất. Giảm giá lên tới 40% cho bộ sưu tập mới."}
          ctaText={banners["home-promo-1"]?.buttonText || "Xem ngay"}
          link={banners["home-promo-1"]?.link || "/products"}
        />

        {renderSection("hot-trend")}
        <HomePromoBanner
          image={getFullImageUrl(banners["home-promo-2"]?.image || PromoImg3)}
          logo={LogoTHS}
          title={banners["home-promo-2"]?.title || "CHÀO HÈ RỰC RỠ"}
          subtitle={banners["home-promo-2"]?.description || "Sẵn sàng cho những chuyến đi trải nghiệm. Cập nhật ngay những mẫu giày bền bỉ, thoáng khí cho mùa hè năng động."}
          ctaText={banners["home-promo-2"]?.buttonText || "Khám phá ngay"}
          link={banners["home-promo-2"]?.link || "/products"}
          reverse={true}
          theme={banners["home-promo-2"]?.theme || "light"}
        />
        {renderSection("most-favorited")}
      </main>

      <ScrollReveal variants="fade-up" threshold={0.2}>
        <CustomerReviews />
      </ScrollReveal>

      <ScrollReveal variants="fade-up" threshold={0.2}>
        <section className="max-w-7xl mx-auto px-4 py-4 md:py-12">
          <SectionHeader
            title="THƯƠNG HIỆU NỔI BẬT"
            subtitle="Đối tác chiến lược"
            accentColor="dark"
          />

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-8 items-center">
            {[
              {
                name: "Nike",
                logo: "https://thesneakerhouse.com/wp-content/uploads/2019/06/nike_PNG19.png",
              },
              {
                name: "Adidas",
                logo: "https://lh5.googleusercontent.com/79z3I82MVZ7u27v2P09u16bYeFr6C2Y8WTknoceRiA0HD1PAiwVOEWdSUSb3uO02RJl1tw8WRiqWby20qjxwocAOBF5fmc7LjTtZ0me3HRX1vYO_kgjh8SELqiCLrO2HiS7fVs35",
              },
              {
                name: "Puma",
                logo: "https://jordan1.vn/wp-content/uploads/2023/09/puma-logo-700x394_4e63cda441424e57a13f698df383f5a8_1024x1024.png",
              },
              {
                name: "Converse",
                logo: "https://jordan1.vn/wp-content/uploads/2023/09/converse-logo-2003-2007-700x394_a3153af49f0c49dc95fd65748d739c87_1024x1024.png",
              },
              {
                name: "Jordan",
                logo: "https://www.elleman.vn/wp-content/uploads/2020/10/08/186364/3-logo-thuong-hieu-elle-man-1020-wiki.png",
              },
              {
                name: "Balenciaga",
                logo: "https://vectorseek.com/wp-content/uploads/2021/01/Balenciaga-Logo-Vector.png",
              },
            ].map((brand, i) => (
              <ScrollReveal
                key={brand.name}
                variants="scale-up"
                delay={i * 0.15}
                distance={0}
              >
                <Link
                  to={`/products?brand=${encodeURIComponent(brand.name)}`}
                  className="flex justify-center items-center p-1 sm:p-6 bg-white dark:bg-slate-900 rounded-2xl border border-transparent hover:border-blue-50 dark:hover:border-slate-800 hover:shadow-xl dark:hover:shadow-blue-900/10 hover:-translate-y-2 transition-all duration-300 group"
                  title={`Xem sản phẩm ${brand.name}`}
                >
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="h-8 sm:h-12 w-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-500 opacity-60 dark:opacity-80 dark:brightness-0 dark:invert group-hover:opacity-100 dark:group-hover:opacity-100"
                  />
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </section>
      </ScrollReveal>
      <LuckyWheel />
    </div>
  );
};

export default Home;
